import { Link } from 'react-router-dom';
import { Layout } from '../components/layout';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { SEO } from '../components/SEO';

const services = [
  {
    id: 'goldilocks',
    number: '01',
    eyebrow: 'GOLDILOCKS ZONE ADVISORY',
    title: 'Goldilocks Zone Advisory',
    outcome: "Your organisation gains clarity on what's enabling and inhibiting change — and a path toward productive balance.",
    description: "Most transformations fail not because leaders lack strategy or execution capability, but because the organisational environment prevents change from taking root. Teams may be trapped in excessive control, chaotic urgency, or structural misalignment. Through observation, interviews, and organisational diagnostics, we assess whether your environment enables or inhibits change. We identify where systems are too rigid, too fragmented, or poorly aligned — and design interventions that move the organisation toward the Goldilocks Zone: the productive balance between structure and autonomy.",
    scope: "Leadership dynamics, governance patterns, decision rights, incentives, and cultural signals that shape how change actually unfolds."
  },
  {
    id: 'operating-model',
    number: '02',
    eyebrow: 'OPERATING MODEL DESIGN',
    title: 'Operating Model Design',
    outcome: "Your structure, governance, and decision rights align with where you're headed — not where you've been.",
    description: "When organisations pursue new strategies but retain legacy structures, ways of working, and governance models, transformation efforts stall. We work with leadership teams to redesign operating models that align structure, accountability, decision rights, and collaboration patterns with strategic intent. The goal is clarity without rigidity — ensuring teams can move quickly while maintaining coherence across the enterprise.",
    scope: "Organisational structure, governance frameworks, accountability models, and cross-team coordination mechanisms."
  },
  {
    id: 'transformation',
    number: '03',
    eyebrow: 'TRANSFORMATION ASSURANCE',
    title: 'Transformation Assurance',
    outcome: "Your leadership maintains genuine visibility over transformation health — not just delivery activity.",
    description: "Large transformation programmes often drift over time as priorities shift, leadership changes, and delivery complexity increases. Without strong oversight, programmes can lose strategic alignment or become overly focused on activity rather than outcomes. We provide independent transformation assurance to assess programme health, governance effectiveness, delivery capability, and strategic alignment. This includes identifying systemic risks, strengthening decision frameworks, and ensuring leadership maintains visibility over transformation progress. The objective is not simply to track delivery, but to ensure the transformation remains meaningful and sustainable.",
    scope: "Programme health assessment, governance effectiveness, delivery capability review, strategic alignment, systemic risk identification."
  },
  {
    id: 'delivery',
    number: '04',
    eyebrow: 'DELIVERY SYSTEM OPTIMISATION',
    title: 'Delivery System Optimisation',
    outcome: "Your teams can move quickly, collaborate effectively, and adapt as priorities evolve.",
    description: "Even well-designed strategies and operating models can fail if the delivery environment prevents teams from executing effectively. Misaligned incentives, fragmented governance, and unclear ownership can slow delivery and create organisational friction. We help organisations design delivery systems that balance autonomy with alignment. This includes improving planning structures, decision pathways, team interfaces, and feedback loops so work flows more effectively across the organisation.",
    scope: "Planning structures, decision pathways, team interfaces, feedback loops, delivery governance."
  },
  {
    id: 'ai-readiness',
    number: '05',
    eyebrow: 'AI READINESS & ADOPTION',
    title: 'AI Readiness & Adoption',
    outcome: "Your organisation adopts AI in a way that augments your people and strengthens your operating model, rather than disrupting both.",
    description: "Most organisations are pursuing AI adoption as a technology initiative when it is fundamentally a transformation challenge. Leadership teams are under pressure to demonstrate AI capability, but without a clear understanding of how AI changes the way work is designed, decisions are made, and teams operate, the result is wasted investment, displaced talent, and shallow adoption that never takes root. We help leaders navigate AI adoption as an organisational change programme. This means assessing workforce readiness, redesigning workflows to integrate AI meaningfully, and ensuring leadership teams can articulate not just what AI tools they are deploying, but how those tools change the way their organisation works. The goal is adoption that strengthens your people and your operating model, not one that hollows out both.",
    scope: "AI strategies that exist as board slides with no execution pathway, workforce displacement disguised as transformation, technology adoption without workflow or role redesign, leadership teams unable to articulate how AI changes the way their organisation operates, and the gap between executive ambition and frontline readiness."
  }
];

const ServiceSection = ({ service, index }) => {
  const [ref, isVisible] = useScrollReveal(0.1);
  const isEven = index % 2 === 0;
  const isFirst = index === 0;

  return (
    <section 
      id={service.id}
      className={`${isFirst ? 'pt-12 pb-20 md:pt-16 md:pb-28' : 'py-20 md:py-28'} ${isEven ? 'bg-white' : 'bg-[#faf9f7]'}`}
      data-testid={`service-section-${service.id}`}
    >
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div 
          ref={ref}
          className={`transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {/* Eyebrow */}
          <p className="text-[#d4782c] font-dm-sans font-semibold text-sm uppercase tracking-wider mb-6">
            {service.number} — {service.eyebrow}
          </p>

          {/* Title */}
          <h2 className="font-playfair text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-6">
            {service.title}
          </h2>

          {/* Outcome */}
          <p className="font-playfair italic text-[#d4782c] text-lg md:text-xl mb-8 leading-relaxed">
            {service.outcome}
          </p>

          {/* Description */}
          <p className="text-[#4a4744] text-base md:text-lg leading-relaxed mb-10">
            {service.description}
          </p>

          {/* Scope */}
          <div className="border-l-[3px] border-[#e5e2dc] pl-6">
            <p className="text-[#1a1a1a] font-dm-sans font-semibold text-sm uppercase tracking-wider mb-3">
              What this typically addresses
            </p>
            <p className="text-[#4a4744] leading-relaxed">
              {service.scope}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

const HeroSection = () => {
  return (
    <section 
      className="min-h-[40vh] flex items-center bg-[#faf9f7]"
      data-testid="services-hero"
    >
      <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16 md:py-24">
        <h1 
          className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold italic text-[#1a1a1a] mb-6 animate-fade-in-up"
          data-testid="services-hero-title"
        >
          How We Help
        </h1>
        <p 
          className="font-dm-sans text-lg md:text-xl text-[#4a4744] leading-relaxed max-w-3xl animate-fade-in-up animate-delay-200"
          data-testid="services-hero-subtitle"
        >
          Five disciplines of organisational transformation — each designed to build your internal capability for lasting change.
        </p>
      </div>
    </section>
  );
};

const CTASection = () => {
  const [ref, isVisible] = useScrollReveal(0.1);

  return (
    <section 
      className="py-24 bg-[#faf9f7]"
      data-testid="services-cta-section"
    >
      <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
        <div 
          ref={ref}
          className={`transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 className="font-playfair text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-4">
            Ready to create the conditions for meaningful change?
          </h2>
          <p className="text-[#4a4744] text-lg mb-10">
            We'd love to understand your challenges and explore how we can help.
          </p>
          <Link 
            to="/contact" 
            className="btn-primary inline-block"
            data-testid="services-cta-button"
          >
            Start a Conversation
          </Link>
        </div>
      </div>
    </section>
  );
};

export default function ServicesPage() {
  return (
    <Layout>
      <SEO 
        title="How We Help Organisations Change"
        path="/services"
        description="Five disciplines of organisational transformation — Goldilocks Zone Advisory, Operating Model Design, Transformation Assurance, Delivery System Optimisation, and AI Readiness & Adoption."
      />
      <HeroSection />
      {services.map((service, index) => (
        <ServiceSection key={service.id} service={service} index={index} />
      ))}
      <CTASection />
    </Layout>
  );
}
